import type { BudgetPlanTemplate, TimeRange } from "@/reducers/domain";
import { BudgetPlanPolicy } from "@redbox-ruby/data-lib";
import clamp from "lodash/clamp";
import moment from "moment";
import {
    RubyBudgetPlanMethod,
    RubyBudgetPlanPolicy,
    RubyBudgetPlanStatus,
    type RubyBasicBudgetPlan,
    type RubyBudgetPlan,
    type RubyBudgetPlanDetails,
    type RubyCampaignId,
    type RubyWeekday,
    type RubyWeekdayBudgets,
} from "../services/backend/RubyData";

export function getActiveBudgetPlan(plans: RubyBasicBudgetPlan[]): RubyBasicBudgetPlan {
    if (plans.length < 1) {
        return null;
    }

    const activePlans = plans.filter((plan) => plan.status === RubyBudgetPlanStatus.ACTIVE);

    const activeByDate = activePlans.find(isActiveBudgetPlan);

    if (activeByDate) {
        return activeByDate;
    }

    const sortedPlans = activePlans.filter((plan) => plan.start < Date.now()).sort((a, b) => a.start - b.start);

    if (sortedPlans.length < 1) {
        return null;
    }

    const lastActive = sortedPlans[sortedPlans.length - 1];

    if ([BudgetPlanPolicy.ROLLOVER_AT_END, BudgetPlanPolicy.REPEATING].includes(lastActive.policy)) {
        return lastActive;
    }

    return null;
}

/**
 * This is only checking if a single plan is in date, not if it has rolled over, as that requires the full list of plans
 */
export function isActiveBudgetPlan(plan: RubyBasicBudgetPlan): boolean {
    return moment.utc().isBetween(moment.utc(plan.start).startOf("day"), moment.utc(plan.end).endOf("day"));
}

export function extendBudgetPlans(plans: RubyBasicBudgetPlan[]): RubyBudgetPlan[] {
    const activePlan = getActiveBudgetPlan(plans);
    return plans.map((plan) => {
        return extendBudgetPlan(plan, activePlan?.id);
    });
}

export function extendBudgetPlan(plan: RubyBasicBudgetPlan, activePlanId: RubyBudgetPlan["id"]): RubyBudgetPlan {
    return {
        ...plan,
        active: plan.id === activePlanId,
        lengthInDays: moment.utc(plan.end).diff(plan.start, "days") + 1,
        daysRemaining: moment.utc(plan.end).diff(moment.utc().startOf("day"), "days"),
        start: moment.utc(plan.start).startOf("day").valueOf(),
        end: moment.utc(plan.end).endOf("day").valueOf(),
    };
}

export function getAllocatedBudget(plan: RubyBudgetPlan): number {
    return (
        plan?.allocations?.reduce((sum, al) => {
            return (
                sum +
                al.details.regions.reduce((sum, region) => {
                    return sum + region.amount;
                }, 0)
            );
        }, 0) ?? null
    );
}

export function getTodaysAllocation(plan: RubyBudgetPlan) {
    const today = moment.utc();
    const todaysAllocation = plan?.allocations?.reduce((latestAllocation, allocation) => {
        if (allocation.createdAt > (latestAllocation?.createdAt ?? 0) && today.isSame(allocation.createdAt, "day")) {
            return allocation;
        }
        return latestAllocation;
    }, null);
    return todaysAllocation;
}

export const DEFAULT_WEEKLY_OVERSPEND = {
    monday: 100,
    tuesday: 100,
    wednesday: 100,
    thursday: 100,
    friday: 100,
    saturday: 100,
    sunday: 100,
};

/**
 * Ordered so the index matches `moment().day()`, letting a date map straight to
 * a weekday key.
 */
const WEEKDAY_NAMES: RubyWeekday[] = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
];

export const DEFAULT_WEEKLY_BUDGETS: RubyWeekdayBudgets = {
    monday: null,
    tuesday: null,
    wednesday: null,
    thursday: null,
    friday: null,
    saturday: null,
    sunday: null,
};

/**
 * A budget plan can vary its spend by day of the week in one of two ways. These
 * are alternatives rather than complementary, so a plan uses one or the other.
 */
export enum WeekdayBudgetMode {
    // Spend a multiple of the plan's calculated daily budget on a given day.
    MULTIPLIER = "multiplier",
    // Spend an explicit amount on a given day.
    AMOUNT = "amount",
}

/**
 * @param details - The details of a saved plan
 * @returns The mode the plan is using, or null when it doesn't vary by day
 */
export function getWeekdayBudgetMode(details: RubyBudgetPlanDetails): WeekdayBudgetMode | null {
    if (hasScheduledWeekdayBudgets(details?.weekdayBudgets)) {
        return WeekdayBudgetMode.AMOUNT;
    }
    if (hasWeekdayOverspend(details?.weekdayOverspend)) {
        return WeekdayBudgetMode.MULTIPLIER;
    }
    return null;
}

/**
 * The amount a plan is scheduled to spend on a given day, which replaces the
 * plan's own daily budget when it is set.
 *
 * Read in UTC to match the day the budget allocation runs against.
 *
 * @param details - The details of a saved plan
 * @param date - The day to read, today if none is given
 * @returns The scheduled amount, or null when that day isn't scheduled
 */
export function getScheduledBudgetForDate(details: RubyBudgetPlanDetails, date = moment.utc()): number | null {
    const amount = details?.weekdayBudgets?.[WEEKDAY_NAMES[moment.utc(date).day()]];
    return amount > 0 ? amount : null;
}

/**
 * Drops the days with no scheduled amount, leaving only the days a plan
 * actually overrides.
 *
 * @param weekdayBudgets - The scheduled budgets held on a plan, if any
 * @returns The scheduled days and their amounts
 */
export function getScheduledWeekdayBudgets(
    weekdayBudgets: RubyWeekdayBudgets | null | undefined
): Record<string, number> {
    return Object.entries(weekdayBudgets ?? {}).reduce<Record<string, number>>((set, [day, amount]) => {
        if (amount > 0) {
            set[day] = amount;
        }
        return set;
    }, {});
}

function hasScheduledWeekdayBudgets(weekdayBudgets: RubyWeekdayBudgets | null | undefined): boolean {
    return Object.values(weekdayBudgets ?? {}).some((amount) => amount > 0);
}

function hasWeekdayOverspend(weekdayOverspend: RubyBudgetPlanDetails["weekdayOverspend"] | null): boolean {
    return Object.values(weekdayOverspend ?? {}).some((percentage) => percentage > MIN_OVERSPEND_PERCENTAGE);
}

/**
 * Builds the details to send to the API. Only the days of the mode in use are
 * sent, so switching mode clears whatever the other mode had configured.
 *
 * @param template - The plan being saved
 * @returns The plan details in the shape the API expects
 */
export function convertTemplateToPlanDetails(template: BudgetPlanTemplate): RubyBudgetPlanDetails {
    return {
        weekdayOverspend: {
            ...DEFAULT_WEEKLY_OVERSPEND,
            ...(template?.weeklyOverspend ?? undefined),
        },
        weekdayBudgets: template?.weeklyBudgets ?? null,
    };
}

// Overspend values are %s starting at 100%. The idea being that the default
// is to spend 100% of their allocation, but some days you can allow overspend

/**
 * The API models weekday overspend as a percentage in excess of 100% (100 = no
 * overspend, 200 = spend double) and rejects anything outside this range. The
 * form works in multipliers, so every conversion has to stay inside these.
 */
export const MIN_OVERSPEND_MULTIPLIER = 1;
export const MAX_OVERSPEND_MULTIPLIER = 10;
const MIN_OVERSPEND_PERCENTAGE = MIN_OVERSPEND_MULTIPLIER * 100;
const MAX_OVERSPEND_PERCENTAGE = MAX_OVERSPEND_MULTIPLIER * 100;

/**
 * The number input has to step in hundredths or multipliers like 1.15 can't be
 * entered, and the browser reports them as invalid.
 */
export const OVERSPEND_MULTIPLIER_STEP = 0.01;

function multiplierToOverspendPercentage(multiplier: number): number | null {
    const value = Number(multiplier);
    if (!Number.isFinite(value) || value <= MIN_OVERSPEND_MULTIPLIER) {
        return null;
    }
    return clamp(Math.round(value * 100), MIN_OVERSPEND_PERCENTAGE, MAX_OVERSPEND_PERCENTAGE);
}

function overspendPercentageToMultiplier(percentage: number): number {
    const value = Number(percentage);
    if (!Number.isFinite(value) || value <= MIN_OVERSPEND_PERCENTAGE) {
        return MIN_OVERSPEND_MULTIPLIER;
    }
    return clamp(Math.round(value), MIN_OVERSPEND_PERCENTAGE, MAX_OVERSPEND_PERCENTAGE) / 100;
}

export enum CampaignAllocationMode {
    PERCENTAGE = "percentage",
    AMOUNT = "amount",
}

export interface CampaignAllocationRow {
    campaignId: RubyCampaignId;
    selected: boolean;
    percentage: number | null;
    amount: number | null;
}

export interface CampaignBudgetAllocation {
    campaignId: RubyCampaignId;
    percentage: number;
}

/** The form values a budget is shared out across campaigns with. */
export interface CampaignAllocationValues {
    totalAmount: number;
    campaignAllocationMode: CampaignAllocationMode;
    campaignAllocations: CampaignAllocationRow[];
}

export const FULL_ALLOCATION_PERCENTAGE = 100;

export const ALLOCATION_PERCENTAGE_STEP = 0.01;

const ALLOCATION_TOLERANCE = 0.005;

export const budgetPlanInitialValues = {
    timeRange: [moment.utc().startOf("day").toDate(), moment.utc().add(30, "days").endOf("day").toDate()] as [
        Date,
        Date
    ],
    totalAmount: null as number,
    weeklyOverspend: {
        monday: 1,
        tuesday: 1,
        wednesday: 1,
        thursday: 1,
        friday: 1,
        saturday: 1,
        sunday: 1,
    },
    weeklyBudgets: { ...DEFAULT_WEEKLY_BUDGETS },
    policy: RubyBudgetPlanPolicy.STOP_AT_END,
    method: RubyBudgetPlanMethod.ADJUST_DAILY,
    setOverspend: false,
    weekdayBudgetMode: WeekdayBudgetMode.MULTIPLIER,
};

export function convertBudgetInputsToPlan(
    values: typeof budgetPlanInitialValues = budgetPlanInitialValues
): BudgetPlanTemplate {
    const usesMultipliers = values.setOverspend && values.weekdayBudgetMode === WeekdayBudgetMode.MULTIPLIER;
    const usesAmounts = values.setOverspend && values.weekdayBudgetMode === WeekdayBudgetMode.AMOUNT;
    return {
        policy: values.policy,
        method: values.method,
        timeRange: [
            moment.utc(values.timeRange[0]).startOf("day").valueOf(),
            moment.utc(values.timeRange[1]).endOf("day").valueOf(),
        ],
        totalAmount: values.totalAmount,
        weeklyOverspend: usesMultipliers
            ? (Object.fromEntries(
                  Object.entries(values.weeklyOverspend)
                      .map(([day, value]) => {
                          const percentage = multiplierToOverspendPercentage(value);
                          if (percentage === null) {
                              return null;
                          }
                          return [day, percentage];
                      })
                      .filter((d) => !!d)
              ) as BudgetPlanTemplate["weeklyOverspend"])
            : null,
        weeklyBudgets: usesAmounts
            ? (Object.fromEntries(
                  Object.keys(DEFAULT_WEEKLY_BUDGETS).map((day) => [
                      day,
                      toScheduledBudget(values.weeklyBudgets?.[day]),
                  ])
              ) as RubyWeekdayBudgets)
            : null,
    };
}

/**
 * The campaigns the budget is being shared with. Rows exist for every campaign
 * the account has, so the ones left unticked are dropped.
 *
 * @param values - The budget being shared out
 * @returns The rows taking a share of it
 */
export function getSelectedAllocationRows(values: CampaignAllocationValues): CampaignAllocationRow[] {
    return (values?.campaignAllocations ?? []).filter((row) => row?.selected && row.campaignId != null);
}

/**
 * The campaigns left out of the split. The whole budget is shared between the
 * campaigns taking a share of it, so these are the ones to stop spending for
 * the team's budgets to add up to the amount being shared.
 *
 * @param values - The budget being shared out
 * @returns The campaigns taking no share of it
 */
export function getUnallocatedCampaignIds(values: CampaignAllocationValues): RubyCampaignId[] {
    return (values?.campaignAllocations ?? [])
        .filter((row) => !row?.selected && row?.campaignId != null)
        .map((row) => row.campaignId);
}

/**
 * The share of the plan a row takes. A fixed amount is read as the percentage
 * of the plan's total it comes to, so both modes split a scheduled day the
 * same way.
 *
 * @param row - The campaign's row in the form
 * @param mode - The mode the split is being entered in
 * @param totalAmount - The plan's own amount, which fixed amounts come out of
 * @returns The percentage of the plan the campaign takes
 */
export function toAllocationPercentage(
    row: CampaignAllocationRow,
    mode: CampaignAllocationMode,
    totalAmount: number
): number {
    if (mode === CampaignAllocationMode.AMOUNT) {
        const amount = Number(row?.amount);
        const total = Number(totalAmount);
        if (!Number.isFinite(amount) || !Number.isFinite(total) || total <= 0) {
            return 0;
        }
        return (amount / total) * FULL_ALLOCATION_PERCENTAGE;
    }
    const percentage = Number(row?.percentage);
    return Number.isFinite(percentage) ? percentage : 0;
}

/**
 * Builds the allocations to split a plan by. Only the mode in use is read, so
 * switching mode ignores whatever the other mode had entered.
 *
 * @param values - The budget being shared out
 * @returns The campaigns' shares, or null when nothing has been shared
 */
export function convertAllocationRowsToAllocations(
    values: CampaignAllocationValues
): CampaignBudgetAllocation[] | null {
    const allocations = getSelectedAllocationRows(values)
        .map((row) => ({
            campaignId: row.campaignId,
            percentage: toAllocationPercentage(row, values.campaignAllocationMode, values.totalAmount),
        }))
        .filter((allocation) => allocation.percentage > 0);

    return allocations.length > 0 ? allocations : null;
}

/**
 * Rewrites the shares in the mode being switched to, so a split entered as
 * percentages carries over to fixed amounts and back rather than the mode
 * switched to showing whatever was last typed into it.
 *
 * A split that doesn't account for the whole budget still doesn't after the
 * switch, as the shares are converted by what they come to between them.
 *
 * @param values - The budget being shared out, as the mode it is leaving
 * @param mode - The mode being switched to
 * @returns The rows, with the shares for that mode filled in
 */
export function convertAllocationRowsToMode(
    values: CampaignAllocationValues,
    mode: CampaignAllocationMode
): CampaignAllocationRow[] {
    const rows = values?.campaignAllocations ?? [];
    const total = Number(values?.totalAmount);
    const usesAmounts = mode === CampaignAllocationMode.AMOUNT;

    const entered = readAllocationShares(rows, usesAmounts ? "percentage" : "amount");
    const existing = readAllocationShares(rows, usesAmounts ? "amount" : "percentage");

    if (!(sumShares(entered) > 0) || !(total > 0)) {
        return rows;
    }

    const readsTheSame = convertShares(existing, total, !usesAmounts).every((share, index) => share === entered[index]);
    if (sumShares(existing) > 0 && readsTheSame) {
        return rows;
    }

    const converted = convertShares(entered, total, usesAmounts);

    return rows.map((row, index) => {
        const share = entered[index] > 0 ? converted[index] : null;
        return usesAmounts ? { ...row, amount: share } : { ...row, percentage: share };
    });
}

/** The share each row holds in one of the two modes, ignoring the rows left out. */
function readAllocationShares(rows: CampaignAllocationRow[], share: "percentage" | "amount"): number[] {
    return rows.map((row) => (row?.selected ? Number(row[share]) || 0 : 0));
}

function sumShares(shares: number[]): number {
    return shares.reduce((sum, share) => sum + share, 0);
}

/**
 * The same split read in the other mode. Only what the shares come to between
 * them is converted, so a split that accounts for half the budget as
 * percentages accounts for half of it as amounts too.
 */
function convertShares(shares: number[], totalAmount: number, toAmounts: boolean): number[] {
    const shared = sumShares(shares);
    if (!(shared > 0)) {
        return shares.map(() => 0);
    }

    const target = toAmounts
        ? (totalAmount * shared) / FULL_ALLOCATION_PERCENTAGE
        : (shared / totalAmount) * FULL_ALLOCATION_PERCENTAGE;

    return distributeAmountByPercentages(
        target,
        shares.map((share) => ({ percentage: (share / shared) * FULL_ALLOCATION_PERCENTAGE }))
    );
}

/**
 * The rows a split opens on for the campaigns already running a budget.
 *
 * Both modes are filled in, each to the precision it is entered in: a share
 * as a percentage to {@link ALLOCATION_PERCENTAGE_STEP}, and as an amount to
 * the budget the campaign is running now.
 *
 * @param campaignPlans - The budget each campaign is running
 * @returns A row per campaign, holding its share in both modes
 */
export function convertPlansToAllocationRows(
    campaignPlans: Array<{ campaignId: RubyCampaignId; totalBudget: number }>
): CampaignAllocationRow[] {
    const plans = (campaignPlans ?? []).filter((plan) => Number(plan?.totalBudget) > 0);

    return deriveAllocationsFromPlans(plans).map((allocation, index) => ({
        campaignId: allocation.campaignId,
        selected: true,
        percentage: allocation.percentage,
        amount: Number(plans[index].totalBudget),
    }));
}

/**
 * The amount the split currently adds up to, in whichever unit it is being
 * entered in. Shown as the user types as well as being validated on save.
 *
 * @param values - The plan being edited
 * @returns The total percentage, or the total amount when using fixed amounts
 */
export function getAllocationTotal(values: CampaignAllocationValues): number {
    const usesAmounts = values?.campaignAllocationMode === CampaignAllocationMode.AMOUNT;
    return getSelectedAllocationRows(values).reduce((sum, row) => {
        const value = Number(usesAmounts ? row.amount : row.percentage);
        return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
}

/**
 * A split has to account for the whole plan, so percentages have to total
 * 100% and fixed amounts have to total the plan's own amount.
 *
 * @param values - The plan being edited
 * @returns The amount the split has to add up to
 */
export function getAllocationTarget(values: CampaignAllocationValues): number {
    if (values?.campaignAllocationMode === CampaignAllocationMode.AMOUNT) {
        return Number(values.totalAmount) || 0;
    }
    return FULL_ALLOCATION_PERCENTAGE;
}

/**
 * @param values - The plan being edited
 * @returns True when the split accounts for the whole plan
 */
export function isAllocationTotalValid(values: CampaignAllocationValues): boolean {
    return Math.abs(getAllocationTotal(values) - getAllocationTarget(values)) < ALLOCATION_TOLERANCE;
}

/**
 * Shares an amount out by percentage so the shares always add back up to it,
 * even when it doesn't divide evenly into whole units of currency.
 *
 * Each share is rounded down first, then the units left over are handed to
 * whichever campaigns were rounded down the furthest, so the same split always
 * produces the same amounts.
 *
 * @param amount - The amount to share out
 * @param allocations - The campaigns' percentages of it
 * @returns Each campaign's amount, in the order the allocations were given
 */
export function distributeAmountByPercentages(
    amount: number,
    allocations: Array<{ campaignId?: RubyCampaignId; percentage: number }>
): number[] {
    const value = Number(amount);
    if (!Number.isFinite(value) || !allocations?.length) {
        return (allocations ?? []).map(() => 0);
    }

    const totalUnits = Math.round(value * 100);
    const shares = allocations.map((allocation, index) => {
        const percentage = Number(allocation?.percentage);
        const exact = (totalUnits * (Number.isFinite(percentage) ? percentage : 0)) / FULL_ALLOCATION_PERCENTAGE;
        const units = Math.floor(exact);
        return { index, units, remainder: exact - units };
    });

    let leftover = totalUnits - shares.reduce((sum, share) => sum + share.units, 0);
    const byLargestRemainder = [...shares].sort((a, b) => b.remainder - a.remainder || a.index - b.index);

    for (const share of byLargestRemainder) {
        if (leftover === 0) {
            break;
        }
        const adjustment = leftover > 0 ? 1 : -1;
        share.units += adjustment;
        leftover -= adjustment;
    }

    return shares.map((share) => share.units / 100);
}

/**
 * The plans a campaign already has over some of a set of dates. A campaign is
 * only allowed one plan for any given day, so a budget can only be planned
 * over dates the campaign has free, other than through the plan it is
 * replacing.
 *
 * @param plans - The campaign's own plans
 * @param timeRange - The dates a budget is being planned over
 * @param excludePlanId - The plan being replaced, which isn't a clash
 * @returns The plans in the way, in the order they were given
 */
export function findOverlappingPlans<T extends Pick<RubyBasicBudgetPlan, "id" | "start" | "end" | "status">>(
    plans: T[],
    timeRange: TimeRange,
    excludePlanId?: T["id"]
): T[] {
    const start = moment.utc(timeRange?.[0]).startOf("day").valueOf();
    const end = moment
        .utc(timeRange?.[1] ?? timeRange?.[0])
        .endOf("day")
        .valueOf();

    return (plans ?? []).filter(
        (plan) =>
            plan?.status !== RubyBudgetPlanStatus.DISABLED &&
            plan?.id !== excludePlanId &&
            moment.utc(plan?.start).startOf("day").valueOf() <= end &&
            moment.utc(plan?.end).endOf("day").valueOf() >= start
    );
}

/**
 * The plan holding a campaign's budget, which is the one its budget is set
 * on rather than being given a second plan alongside it.
 *
 * The budget a campaign runs is whichever plan it is spending today, whatever
 * dates or policy that plan has, as that is the budget the team's pages count
 * and total up. A campaign spending nothing today keeps a plan it is due to
 * spend over the dates being planned, as it can't hold two plans over the
 * same days.
 *
 * @param plans - The campaign's own plans
 * @param timeRange - The dates a budget is being planned over
 * @returns The plan to set the budget on, or null to plan a new one
 */
export function findCampaignBudgetPlan(plans: RubyBasicBudgetPlan[], timeRange: TimeRange): RubyBasicBudgetPlan | null {
    const livePlans = (plans ?? []).filter((plan) => plan?.status !== RubyBudgetPlanStatus.DISABLED);
    return getActiveBudgetPlan(livePlans) ?? findOverlappingPlans(livePlans, timeRange)[0] ?? null;
}

/**
 * Keeps a row for every campaign the account has, so the whole account can be
 * split from one place, without losing the shares already entered.
 *
 * The campaign the plan belongs to always takes a share, as its own plan is
 * the one being saved.
 *
 * @param rows - The rows the form is holding
 * @param campaignIds - Every campaign that can take a share
 * @param campaignId - The campaign the plan belongs to
 * @returns A row per campaign, in the order the campaigns were given
 */
export function syncAllocationRows(
    rows: CampaignAllocationRow[] | null | undefined,
    campaignIds: RubyCampaignId[],
    campaignId: RubyCampaignId
): CampaignAllocationRow[] {
    return (campaignIds ?? []).map((id) => {
        const isPlansCampaign = id === campaignId;
        const row = (rows ?? []).find((r) => r?.campaignId === id);
        if (row) {
            return {
                ...row,
                selected: isPlansCampaign || !!row.selected,
            };
        }
        return {
            campaignId: id,
            selected: isPlansCampaign,
            percentage: isPlansCampaign ? FULL_ALLOCATION_PERCENTAGE : null,
            amount: null,
        };
    });
}

/**
 * Reads the split a set of campaigns is already running, so re-opening a plan
 * shows the shares the campaigns actually have rather than starting over.
 *
 * @param campaignPlans - The plan each campaign is running over the dates
 * @returns Each campaign's share of what those plans spend
 */
export function deriveAllocationsFromPlans(
    campaignPlans: Array<{ campaignId: RubyCampaignId; totalBudget: number }>
): CampaignBudgetAllocation[] {
    const plans = (campaignPlans ?? []).filter((plan) => Number(plan?.totalBudget) > 0);
    const total = plans.reduce((sum, plan) => sum + Number(plan.totalBudget), 0);

    if (total <= 0) {
        return [];
    }

    // Shares are entered to two decimal places, as amounts of currency are, so
    // budgets that don't divide evenly are rounded to a share that can be
    // typed back in and still add up to the whole budget.
    const percentages = distributeAmountByPercentages(
        FULL_ALLOCATION_PERCENTAGE,
        plans.map((plan) => ({
            campaignId: plan.campaignId,
            percentage: (Number(plan.totalBudget) / total) * FULL_ALLOCATION_PERCENTAGE,
        }))
    );

    return plans.map((plan, index) => ({
        campaignId: plan.campaignId,
        percentage: percentages[index],
    }));
}

/**
 * The budget a day would get before any weekday settings are applied. A
 * repeating plan's amount is already a daily amount, so it isn't spread out.
 *
 * @param values - The plan being edited
 * @returns The plan's daily budget
 */
export function getBaseDailyRate(values: BudgetPlanTemplate): number {
    const range = values.timeRange;
    if (values.policy === RubyBudgetPlanPolicy.REPEATING) {
        return values.totalAmount;
    }
    const rangeDays = moment.utc(range[1]).endOf("day").diff(moment.utc(range[0]).startOf("day"), "days") + 1;
    return values.totalAmount / rangeDays;
}

/**
 * Days left blank are sent as null so they keep following the plan's daily
 * budget, rather than being scheduled to spend nothing.
 */
function toScheduledBudget(amount: number | null | undefined): number | null {
    const value = Number(amount);
    if (amount == null || !Number.isFinite(value) || value <= 0) {
        return null;
    }
    return value;
}

export function convertBudgetTemplateToValues(plan: BudgetPlanTemplate): typeof budgetPlanInitialValues {
    if (!plan) {
        return budgetPlanInitialValues;
    }
    const overspends = Object.entries(plan.weeklyOverspend ?? {});
    const hasOverspend = overspends.some(([_day, value]) => value > MIN_OVERSPEND_PERCENTAGE);
    const hasBudgets = hasScheduledWeekdayBudgets(plan.weeklyBudgets);
    return {
        policy: plan.policy,
        method: plan.method,
        timeRange: [new Date(plan.timeRange[0]), new Date(plan.timeRange[1])],
        setOverspend: hasOverspend || hasBudgets,
        weekdayBudgetMode: hasBudgets ? WeekdayBudgetMode.AMOUNT : WeekdayBudgetMode.MULTIPLIER,
        totalAmount: plan.totalAmount,
        weeklyOverspend: {
            ...budgetPlanInitialValues.weeklyOverspend,
            ...(Object.fromEntries(
                overspends.map(([day, value]) => [day, overspendPercentageToMultiplier(value)])
            ) as BudgetPlanTemplate["weeklyOverspend"]),
        },
        weeklyBudgets: {
            ...DEFAULT_WEEKLY_BUDGETS,
            ...(Object.fromEntries(
                Object.entries(plan.weeklyBudgets ?? {}).map(([day, amount]) => [day, toScheduledBudget(amount)])
            ) as RubyWeekdayBudgets),
        },
    };
}

export function convertBudgetPlanToTemplate(plan: RubyBudgetPlan): BudgetPlanTemplate {
    if (!plan) {
        return convertBudgetInputsToPlan();
    }
    return {
        policy: plan.policy,
        method: plan.method,
        timeRange: [plan.start, plan.end],
        totalAmount: plan.totalBudget,
        weeklyOverspend: {
            ...DEFAULT_WEEKLY_OVERSPEND,
            ...(plan.details?.weekdayOverspend ?? {}),
        },
        weeklyBudgets: {
            ...DEFAULT_WEEKLY_BUDGETS,
            ...(plan.details?.weekdayBudgets ?? {}),
        },
    };
}

export function convertBudgetPlanToValues(plan: RubyBudgetPlan): typeof budgetPlanInitialValues {
    const template = convertBudgetPlanToTemplate(plan);
    return convertBudgetTemplateToValues(template);
}
